import React, { useState, useEffect } from 'react';
import { Button, Drawer } from 'aha-components';
import GA from 'react-ga4';
import { isMobile, getOrderSource } from '@/biz/tool';
import WebModal from './Modal';
import BottomDrawer from './BottomDrawer';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import ModalAnalysisBreadcrumb from './ModalAnalysisBreadcrumb';
import InterestContent from './InterestContent';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import axiosInstance from '../../../apis/axiosInstance';
import { AuthorizeErrorIcon } from '@/components/common/icon';
import toast from 'react-hot-toast';
import Radio from '@/components/Radio/index';
import { useOrderListStore } from '@/hooks/useOrderListStore';
import { NationsLab } from '@/utils/locations';
import { ProductType, ProductPhysicalSelectEnum } from '../config';
import { useTranslation } from 'react-i18next';
import {
  MOBILE_DRAWER_HEIGHT,
  calcViewportHeight
} from '@/utils/viewportHeight';

const ModalShowAnInterest: React.FC = () => {
  const { t } = useTranslation();
  const { showModalSubmitInterest, setShowModalSubmitInterest } =
    useWebModalStore();

  return (
    <Drawer
      title={t('campaign.modal.submit_interest.title', {
        defaultValue: 'Submit interest'
      })}
      open={showModalSubmitInterest}
      onClose={() => setShowModalSubmitInterest(false)}
      height={MOBILE_DRAWER_HEIGHT}
      placement={isMobile() ? 'bottom' : 'right'}
      width={isMobile() ? '100%' : 810}
    >
      <ModalContent />
    </Drawer>
  );
};

const ModalContent: React.FC = () => {
  const { t } = useTranslation();
  const { reQueryDetailData, detailData } = useDetailDataStore();
  const { reQueryOrderListData } = useOrderListStore();
  const { order, payment, product } = detailData;
  const {
    setShowModalSubmitInterest,
    setShowModalMissOut,
    skipScript,
    isAgree,
    setIsAgree,
    isShowAgreement,
    setIsShowAgreement,
    setShowModalSelectInvitedOrderByNoMatch,
    setShowModalSelectInvitedOrderByNoMatchType,
    setEducationModal,
    agreeProductShip,
    setModalNotMatchInfo,
    productShippingCountry
  } = useWebModalStore();
  const [joinBtnLoading, setJoinBtnLoading] = useState(false);
  const { selectedPriceFields, rateNonNegotiable } = useMultiplePricesStore();
  const [noticeAgree, setNoticeAgree] = useState(false);
  const [productShipNotive, setProductShipNotive] = useState(false);
  const [selectCountryError, setSelectCountryError] = useState(false);

  const joinCampaign = async () => {
    const orderId = order.id;
    const campaignId = order.campaignId;
    const platform = order.platform;
    setJoinBtnLoading(true);
    try {
      const params: any = {
        campaignId,
        platform,
        orderId
      };

      if (selectedPriceFields?.length > 0) {
        // 删除之前遗留的 inputPrice 字段 判断逻辑 简化代码
        params.interestedPriceFields = selectedPriceFields;
      }
      if (skipScript) {
        params.skipScript = true;
      }
      if (productShippingCountry) {
        params.productShippingCountry = productShippingCountry;
      }
      params.nonNegotiable = rateNonNegotiable;
      const { data } = await axiosInstance.post(`/campaign/join`, params);
      if (
        !data?.data?.success &&
        data?.data?.message &&
        data?.data?.message === 'not_match'
      ) {
        setShowModalSubmitInterest(false);
        setModalNotMatchInfo({
          visible: true,
          message: data?.data?.detail
        });
        return;
      }
      // GA 上报 join campaign 成功事件
      const orderSource = getOrderSource(orderId);
      GA.gtag('event', 'join_campaign', {
        order_id: orderId,
        campaign_id: campaignId,
        order_source: orderSource, // dm | email | default
        category: platform
      });

      setShowModalSubmitInterest(false);
      if (data?.data?.isFirstOrder) {
        await reQueryDetailData({ orderId });
        setEducationModal(true, detailData);
        return;
      }
      toast.success(
        t('campaign.modal.submit_interest.success_message', {
          defaultValue: 'You have successfully applied!'
        }),
        { duration: 2000 }
      );

      // 请求 invited 订单
      setShowModalSelectInvitedOrderByNoMatchType('success');
      setShowModalSelectInvitedOrderByNoMatch(true);
      await reQueryOrderListData();
      // end 请求 invited 订单

      // if (duringFirstOrder) {
      //   // 首单 不走如下步骤
      //   return;
      // }
      if (payment) {
        reQueryDetailData({ orderId });
      } else {
        setTimeout(() => {
          window.location.href = `/campaign/wallet/${orderId}`;
        }, 2000);
      }
    } catch (error: any) {
      if (error.code === 40010) {
        // The campaign quota is full: Join quota is not enough
        toast.error(
          t('campaign.modal.submit_interest.quota_full_error', {
            defaultValue:
              'We sincerely apologize, but this campaign has reached its capacity and no longer has available spots. Thank you for your understanding!'
          })
        );
        reQueryDetailData({ orderId });
      } else {
        toast.error(error.message);
      }
      console.error(`Failed to fetch : (/campaign/join) \n`, error);
    } finally {
      setJoinBtnLoading(false);
    }
  };
  const isProductShip =
    product?.productType === ProductType.PHYSICAL_PRODUCT &&
    product?.physicalProductShipType === ProductPhysicalSelectEnum.ship;

  return (
    <div
      className="w-full flex flex-col items-start gap-3"
      style={
        isMobile()
          ? {
              height: calcViewportHeight(
                '64px',
                'env(safe-area-inset-top, 0px)'
              )
            }
          : undefined
      }
    >
      <ModalAnalysisBreadcrumb index={2} />
      {noticeAgree ? (
        <div className="w-full h-12 px-2 py-3 bg-[#ffebe5] text-red-500 rounded-lg flex gap-2">
          <AuthorizeErrorIcon className="w-6 h-6" />
          {t('campaign.modal.submit_interest.agreement_notice', {
            defaultValue: 'Please agree to the Creator Agreement.'
          })}
        </div>
      ) : null}
      {productShipNotive ? (
        <div className="w-full h-12 px-2 py-3 bg-[#ffebe5] text-red-500 rounded-lg flex gap-2">
          <AuthorizeErrorIcon className="w-6 h-6" />
          {t('campaign.modal.submit_interest.location_notice', {
            defaultValue: 'Please confirm locations.'
          })}
        </div>
      ) : null}
      <div
        className={`w-full md:overflow-auto flex flex-col gap-6
          ${isMobile() && !noticeAgree ? 'pb-[150px]' : ''}
          ${isMobile() && noticeAgree ? 'h-[calc(100vh-290px)]  pb-10' : ''}
          `}
      >
        <InterestContent
          selectCountryError={selectCountryError}
          setSelectCountryError={setSelectCountryError}
        />
      </div>
      {/*  */}
      <div
        className={`bg-white w-full flex justify-end items-center gap-4 border-t border-solid border-[#e8e8ec] pt-4 mt-2
            ${isMobile() ? 'justify-center fixed bottom-0 left-0 pb-[calc(16px+env(safe-area-inset-bottom,0px))]' : ''}
          `}
      >
        <div className="flex flex-col gap-4 px-4">
          <div
            className="gap-2"
            style={{ display: isMobile() ? 'flex' : 'none' }}
          >
            <Radio
              checked={isAgree}
              onClick={() => {
                setIsAgree(!isAgree);
              }}
            />
            <div className="w-full">
              <span className="ml-1">
                {t('campaign.modal.submit_interest.consent_text', {
                  defaultValue: "I consent to AhaCreator's"
                })}
              </span>
              <span
                className="inline-block ml-1 cursor-pointer text-[#0C67E6] underline"
                onClick={() => setIsShowAgreement(!isShowAgreement)}
              >
                {t('campaign.modal.submit_interest.creator_agreement', {
                  defaultValue: 'Creator Agreement'
                })}
              </span>
            </div>
          </div>
          <div className="flex gap-4">
            <Button
              className="!w-[160px] !h-10"
              onClick={() => {
                setShowModalSubmitInterest(false);
                setShowModalMissOut(true);
              }}
            >
              {t('campaign.modal.submit_interest.cancel_btn', {
                defaultValue: 'Cancel'
              })}
            </Button>
            <Button
              className="!w-[160px] !h-10"
              type="primary"
              onClick={() => {
                if (joinBtnLoading) {
                  return;
                }
                if (!isAgree) {
                  if (isMobile()) {
                    setNoticeAgree(true);
                  } else {
                    toast.error(
                      t('campaign.modal.submit_interest.agreement_notice', {
                        defaultValue: 'Please agree to the Creator Agreement.'
                      })
                    );
                  }
                  return;
                } else {
                  setNoticeAgree(false);
                }
                if (!agreeProductShip && isProductShip) {
                  if (isMobile()) {
                    setProductShipNotive(true);
                  } else {
                    setSelectCountryError(true);
                    toast.error(
                      t('campaign.modal.submit_interest.location_notice', {
                        defaultValue: 'Please confirm locations.'
                      })
                    );
                  }
                  return;
                } else {
                  setProductShipNotive(false);
                }
                joinCampaign();
              }}
              loading={joinBtnLoading}
            >
              {t('campaign.modal.submit_interest.submit_btn', {
                defaultValue: 'Submit interest'
              })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalShowAnInterest;
